import React, { useState, useEffect } from 'react';
import StatCard from '../components/StatCard';
import { FaUsers, FaStore, FaReceipt, FaChartLine, FaCrown, FaGem } from 'react-icons/fa';
import { FaShieldHalved, FaRocket, FaHeadset, FaArrowUpRightFromSquare } from 'react-icons/fa6';
import api from '../services/api';

const HomePage = () => {
  const [stats, setStats] = useState({
    users: 0,
    shops: 0,
    transactions: 0,
  });
  const [shops, setShops] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typewriterText, setTypewriterText] = useState('');
  
  const fullText = "Zarządzaj swoim sklepem w jednym miejscu. Twórz, sprzedawaj i analizuj. Możesz też kupywać przedmioty w innych sklepach stworzonych przez użytkowników naszej platformy.";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [statsRes, shopsRes] = await Promise.all([
          api.get('/stats'),
          api.get(`/shops?search=${searchTerm}`)
        ]);
        setStats(statsRes.data);
        setShops(shopsRes.data);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Nie udało się pobrać danych');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    const interval = setInterval(() => {
      api.get('/stats').then(res => setStats(res.data)).catch(err => console.error('Failed to refresh stats:', err));
    }, 30000);

    return () => clearInterval(interval);
  }, [searchTerm]);

  // Typewriter effect
  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index < fullText.length) {
        setTypewriterText(fullText.slice(0, index + 1));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 50);

    return () => clearInterval(timer);
  }, []);

  const LoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="spinner mb-4"></div>
      <p className="text-gray-300 animate-pulse">Ładowanie statystyk...</p>
    </div>
  );

  const ErrorMessage = () => (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="text-red-400 text-6xl mb-4">⚠️</div>
      <p className="text-red-400 text-lg">{error}</p>
      <button
        onClick={() => window.location.reload()}
        className="btn-primary mt-4"
      >
        Spróbuj ponownie
      </button>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="text-center mb-16 animate-fadeInUp">
        <h1 className="text-6xl font-bold gradient-text tracking-tight mb-4">
          Witaj na KrukShop
        </h1>
        <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed min-h-[3rem]">
          {typewriterText}
          <span className="animate-pulse">|</span>
        </p>
        
        {/* Floating elements */}
        <div className="relative mt-8">
          <div className="absolute left-1/4 top-0 w-2 h-2 bg-blue-400 rounded-full animate-pulse-slow opacity-60"></div>
          <div className="absolute right-1/3 top-4 w-1 h-1 bg-purple-400 rounded-full animate-pulse-slow opacity-80" style={{animationDelay: '1s'}}></div>
          <div className="absolute left-2/3 top-2 w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse-slow opacity-70" style={{animationDelay: '2s'}}></div>
        </div>
      </div>

      {/* Stats Section */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage />
      ) : (
        <div className="space-y-12">
          {/* Main Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <StatCard
              title="Zarejestrowani użytkownicy"
              value={stats.users}
              icon={<FaUsers />}
              color="blue"
              delay={100}
            />
            <StatCard
              title="Aktywne sklepy"
              value={stats.shops}
              icon={<FaStore />}
              color="green"
              delay={200}
            />
            <StatCard
              title="Sprzedane przedmioty"
              value={stats.transactions}
              icon={<FaReceipt />}
              color="purple"
              delay={300}
            />
          </div>

          {/* Additional Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StatCard
              title="Wzrost sprzedaży"
              value={`+${Math.round((stats.transactions / stats.users * 100) || 0)}%`}
              icon={<FaChartLine />}
              color="indigo"
              delay={400}
            />
            <StatCard
              title="Premium użytkownicy"
              value={Math.round(stats.users * 0.15) || 0}
              icon={<FaCrown />}
              color="yellow"
              delay={500}
            />
            <StatCard
              title="Średnia wartość"
              value={`${Math.round((stats.transactions * 45.99) / (stats.transactions || 1))}zł`}
              icon={<FaGem />}
              color="red"
              delay={600}
            />
          </div>

          {/* Shop List Section */}
          <div className="mt-16 animate-fadeInUp" style={{animationDelay: '700ms'}}>
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Przeglądaj Sklepy</h2>
            <input
              type="text"
              placeholder="Szukaj sklepu po nazwie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full max-w-lg mx-auto block p-3 mb-8 bg-white/5 text-white rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            />
            <div className="flex space-x-6 overflow-x-auto pb-4 -mx-6 px-6">
              {shops.length > 0 ? shops.map(shop => (
                <a href={`/shop/${shop.slug}`} key={shop._id} className="flex-shrink-0 w-80 bg-gray-800 rounded-lg overflow-hidden transition-all duration-300 transform hover:-translate-y-2 shadow-lg hover:shadow-2xl">
                  <div
                    className="h-32 bg-cover bg-center relative"
                    style={{
                      backgroundImage: shop.customization?.bannerUrl
                        ? `url(${shop.customization.bannerUrl})`
                        : `linear-gradient(to right, ${shop.customization?.themeColor || '#1e3a8a'}, ${shop.customization?.secondaryColor || '#3b82f6'})`
                    }}
                  >
                    <span className={`absolute bottom-2 right-2 px-2 py-0.5 text-xs rounded-full ${shop.isPremium ? 'bg-yellow-500/20 text-yellow-300' : 'bg-gray-500/20 text-gray-300'}`}>
                      {shop.isPremium ? 'Premium' : 'Standard'}
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="relative z-10 flex items-center -mt-16">
                      <img src={shop.customization?.logoUrl ? `${shop.customization.logoUrl}` : 'https://placehold.co/64'} alt={shop.name} className="w-16 h-16 flex-shrink-0 bg-gray-700 rounded-full object-cover border-4 border-gray-800"/>
                      <div className="ml-4 flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-white truncate">{shop.name}</h3>
                      </div>
                    </div>
                    <p className="text-gray-400 mt-6 text-sm truncate">{shop.serverDescription || 'Brak opisu'}</p>
                  </div>
                </a>
              )) : (
                <div className="w-full text-center text-gray-400 py-8">
                  <p>Nie znaleziono sklepów pasujących do Twojego wyszukiwania.</p>
                </div>
              )}
            </div>
          </div>

          {/* Dlaczego My Section */}
          <div className="mt-24 animate-fadeInUp" style={{animationDelay: '800ms'}}>
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Dlaczego My?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="glass p-8 rounded-xl text-center hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaShieldHalved className="text-3xl text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Bezpieczeństwo</h3>
                <p className="text-gray-300">Najwyższy poziom zabezpieczeń dla Twoich transakcji i danych osobowych.</p>
              </div>
              <div className="glass p-8 rounded-xl text-center hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaRocket className="text-3xl text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Szybkość</h3>
                <p className="text-gray-300">Błyskawiczne przetwarzanie zamówień i natychmiastowa dostawa produktów.</p>
              </div>
              <div className="glass p-8 rounded-xl text-center hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaHeadset className="text-3xl text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Wsparcie 24/7</h3>
                <p className="text-gray-300">Nasz zespół jest dostępny przez całą dobę, aby pomóc w każdej sytuacji.</p>
              </div>
            </div>
          </div>

          {/* Nasz Team Section */}
          <div className="mt-24 animate-fadeInUp" style={{animationDelay: '900ms'}}>
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Nasz Team</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="glass p-8 rounded-xl text-center hover:scale-105 transition-transform duration-300">
                <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white"><img src="./ojando_avatar-modified.png"></img></span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Ojando</h3>
                <p className="text-blue-400 mb-4">WŁAŚCICIEL</p>
                <p className="text-gray-300 text-sm">Od ponad 3 lat aktywnie zajmuje się robieniem serwerów minecraft. Odpowiada za rozwój oraz optymalizację platformy ItemShop. </p>
              </div>
              <div className="glass p-8 rounded-xl text-center hover:scale-105 transition-transform duration-300">
                <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-blue-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white"><img src="./olekszef_avatar-modified.png"></img></span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">OlekSZEF_14</h3>
                <p className="text-green-400 mb-4">WSPÓŁ WŁAŚCICIEL</p>
                <p className="text-gray-300 text-sm">Najlepszy pomocnik, można mu zaufać.</p>
              </div>
            </div>
          </div>

          {/* Nasze Usługi Section */}
          <div className="mt-24 animate-fadeInUp" style={{animationDelay: '1000ms'}}>
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Nasze Usługi</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <a
                href="https://bambusmc.ovh"
                target="_blank"
                rel="noopener noreferrer"
                className="glass p-8 rounded-xl text-center hover:scale-105 transition-all duration-300 group cursor-pointer"
              >
                <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-orange-500/30 transition-colors">
                  <FaArrowUpRightFromSquare className="text-3xl text-orange-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">BAMBUSMC.OVH</h3>
                <p className="text-gray-300 mb-4">Profesjonalny serwer minecraft. Niezawodność i wydajność na najwyższym poziomie.</p>
                <span className="text-orange-400 text-sm font-semibold">Odwiedź stronę →</span>
              </a>
              <div className="glass p-8 rounded-xl text-center hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaStore className="text-3xl text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Sklepy Premium</h3>
                <p className="text-gray-300 mb-4">Zaawansowane funkcje dla Twojego sklepu. Więcej możliwości, lepsze wyniki.</p>
                <span className="text-blue-400 text-sm font-semibold">Wkrótce dostępne</span>
              </div>
              <div className="glass p-8 rounded-xl text-center hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaChartLine className="text-3xl text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Analityka Pro</h3>
                <p className="text-gray-300 mb-4">Szczegółowe raporty i analizy sprzedaży. Podejmuj lepsze decyzje biznesowe.</p>
                <span className="text-green-400 text-sm font-semibold">W przygotowaniu</span>
              </div>
            </div>
          </div>

          {/* Status indicator */}
          <div className="fixed bottom-6 right-6 glass rounded-full p-3 animate-fadeInUp" style={{animationDelay: '1100ms'}}>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-300">System testowany</span>
              {/* <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-300">System aktywny</span> */}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default HomePage;